// Twilio specfic functions
import {
  connect, createLocalTracks, isSupported, Logger,
} from 'twilio-video';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import assign from 'lodash/assign';
import * as VideoProcessors from '@twilio/video-processors';

export const isVideoSupported = isSupported;

export const getMediaStream = async function (kind, deviceId) {
  const [track] = await createLocalTracks({ [kind]: { deviceId } });
  return {
    track,
    stream: new MediaStream([track.mediaStreamTrack]),
  };
};

const BLUR_DEFAULT_MASK_RADIUS = 10;
const BLUR_DEFAULT_FILTER_RADIUS = 5;
const getBlurFilter = async function (config) {
  const filterConfig = assign(
    {
      assetsPath: '',
      maskBlurRadius: BLUR_DEFAULT_MASK_RADIUS,
      blurFilterRadius: BLUR_DEFAULT_FILTER_RADIUS,
    },
    config,
  );
  const blurFilter = new VideoProcessors.GaussianBlurBackgroundProcessor(filterConfig);
  await blurFilter.loadModel();

  return blurFilter;
};

/**
 * VERTEX_SHADER error solution:
 * https://github.com/twilio/twilio-video-processors.js/issues/62#issuecomment-1610881259
 */
const processorOption = {
  inputFrameBufferType: "video",
  outputFrameBufferContextType: "webgl2",
};

export const setBlur = async function (track, config) {
  if (track.kind === 'video') {
    track.addProcessor(await getBlurFilter(config), processorOption);
  }
};

export const unsetBlur = async function (track) {
  if (track.kind === 'video') {
    track.removeProcessor(track.processor);
  }
};

/**
 * This works with local resource only and it does not work with images
 * from different domain. The relevant ticket is filed in the video processor issues as below.
 *
 * https://github.com/twilio/twilio-video-processors.js/issues/52
 */
export const setBackground = async function (track, config) {
  const imageUrl = get(config, 'backgroundImageUrl');

  if (isEmpty(imageUrl)) {
    throw new Error(`Unable to set image with ${imageUrl}`);
  }

  return new Promise(async (resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    img.onload = async () => {
      try {
        const bg = new VideoProcessors.VirtualBackgroundProcessor({
          assetsPath: '',
          backgroundImage: img,
          maskBlurRadius: 5,
        });
        await bg.loadModel();

        track.addProcessor(bg, processorOption);
        resolve();
      } catch (err) {
        reject(err);
      }
    };
  });
};

export const getParticipantId = function (participant) {
  return participant.sid;
};

export const bootstrapRoom = function (room, options) {
  const {
    onNewParticipantCb,
    onDeleteParticipantCb,
    onRoomDisconnection,
  } = options;

  const trackPublished = (publication, participant) => {
    // If the TrackPublication is already subscribed to, then attach the Track to the DOM.
    if (publication.track) {
      onNewParticipantCb(publication.track, participant);
    }

    // Once the TrackPublication is subscribed to, attach the Track to the DOM.
    publication.on('subscribed', track => {
      onNewParticipantCb(track, participant);
    });

    // Once the TrackPublication is unsubscribed from, detach the Track from the DOM.
    publication.on('unsubscribed', track => {
      onDeleteParticipantCb(participant, track);
    });
  };

  const participantConnected = (participant) => {
    // Handle the TrackPublications already published by the Participant.
    participant.tracks.forEach(publication => {
      trackPublished(publication, participant);
    });

    // Handle theTrackPublications that will be published by the Participant later.
    participant.on('trackPublished', publication => {
      trackPublished(publication, participant);
    });
  };

  participantConnected(room.localParticipant, room);

  room.participants.forEach(participant => {
    participantConnected(participant);
  });

  room.on('participantConnected', participant => {
    participantConnected(participant);
  });

  room.on('participantDisconnected', participant => {
    onDeleteParticipantCb(participant);
  });

  room.once('disconnected', (/* room, error */) => {
    // Handle the disconnected LocalParticipant.
    onDeleteParticipantCb(room.localParticipant);

    onRoomDisconnection(room);
  });
};

/**
 * Connection Handler that determines whether
 * a participant is disconnected or reconnected by looking at a time gap
 * between disconnection and connection
 */
const UPDATE_DELAY = 500; // 500ms time gap
const TRACK_KIND = {
  VIDEO: 'video',
  AUDIO: 'audio',
};

class ConnectionHandler {
  constructor(cb) {
    this.callback = cb;
    this.participantList = {};
    this.removalTimer = {};
  }

  areVideoAudioDisconnected(identity) {
    return !this.participantList[identity][TRACK_KIND.VIDEO]
      && !this.participantList[identity][TRACK_KIND.AUDIO];
  }

  addParticipant(participant, track) {
    const identity = get(participant, 'identity');
    const kind = get(track, 'kind');
    if (!identity || !kind) {
      return;
    }

    if (!has(this.participantList, identity)) {
      this.participantList[identity] = { video: false, audio: false };
    }

    const isNewConnection = this.areVideoAudioDisconnected(identity);
    this.participantList[identity][kind] = true;

    if (has(this.removalTimer, identity)) {
      clearTimeout(this.removalTimer[identity]);
      this.removalTimer[identity] = null;
      this.callback('reconnected', { identity });
    } else if (isNewConnection) {
      this.callback('newly-connected', { identity });
    }
  }

  removePaticipant(participant, track) {
    const identity = get(participant, 'identity');
    const kind = get(track, 'kind');
    if (!identity || !kind) {
      return;
    }

    this.participantList[identity][kind] = false;

    if (this.areVideoAudioDisconnected(identity)) {
      this.removalTimer[identity] = setTimeout(() => {
        // Trigger a participt is removed
        this.removalTimer[identity] = null;
        this.callback('disconnected', { identity });
      }, UPDATE_DELAY);
    }
  }
}

// eslint-disable-next-line max-len
export const joinRoom = async function (token, connectOptions, selfElement, remoteElement, debugVideo, onConnectionEventCb) {
  const logger = Logger.getLogger('twilio-video');
  logger.setLevel(debugVideo ? 'debug' : 'error');

  const room = await connect(token, connectOptions);
  const { localParticipant } = room;
  const connectionHandler = new ConnectionHandler((ev, { identity }) => {
    onConnectionEventCb(identity, ev);
  });

  bootstrapRoom(room, {
    onNewParticipantCb: async (track, participant) => {
      if (participant === localParticipant) {
        if (track.kind === 'video' && !isEmpty(get(connectOptions, 'video.blur'))) {
          await setBlur(track, get(connectOptions, 'video.blur', null));
        } else if (track.kind === 'video' && !isEmpty(get(connectOptions, 'video.backgroundImageUrl'))) {
          await setBackground(track, get(connectOptions, 'video', {}));
        }
        track.attach(selfElement);
      } else {
        track.attach(remoteElement);
      }
      connectionHandler.addParticipant(participant, track);
    },
    onDeleteParticipantCb: (participant, track) => {
      connectionHandler.removePaticipant(participant, track);
    },
    onRoomDisconnection: (/* room */) => {
    },
  });

  return room;
};

export const leaveRoom = function (room) {
  const { localParticipant } = room;

  ['audioTracks', 'videoTracks'].forEach(trackName => {
    localParticipant[trackName].forEach(publication => {
      if (publication[1] && publication[1].track) {
        publication[1].track.stop();
      }
    });
  });

  room.disconnect();
};

export const mute = function (room, muted, type = 'video') {
  const { localParticipant } = room;
  if (type === 'audio') {
    localParticipant.audioTracks.forEach((publication) => {
      if (muted) {
        publication.track.disable();
      } else {
        publication.track.enable();
      }
    });
  }

  if (type === 'video') {
    localParticipant.videoTracks.forEach((publication) => {
      if (muted) {
        publication.track.disable();
      } else {
        publication.track.enable();
      }
    });
  }
};

export default {
  isVideoSupported,
  getMediaStream,
  getParticipantId,
  joinRoom,
  bootstrapRoom,
  leaveRoom,
  mute,
  setBlur,
  unsetBlur,
  setBackground,
};
